import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, map, tap } from 'rxjs';
import { ApiService } from '../../../../core/api/api.service';
import { ENDPOINTS } from '../../../../core/api/endpoints';
import { API_HOST } from '../../../../core/api/api.config';
import {
  CreditProposalForLetterDto,
  CreditTermParamsDto,
  FacilityOfferLetterParamsDto,
} from '../models/credit-proposal-letter.model';
import {
  CovenantResponseDto,
  ProposedDisbursementListRowDto,
  ProposedDisbursementNoteDto,
  CadResponseDto,
  CreditGuarantorsResponseDto,
  DisbursementGuarantorDto,
  GuarantorLimitUtilizedDto,
  SaveDisbursementGuarantorPayload,
  SaveCadResponseItem,
  SaveCovenantResponsesPayload,
  SubmitDisbursementNotePayload,
} from '../models/proposed-disbursement.model';

export interface SubmitCreditProposalPayload {
  entySeq: number;
  entyType: number;
  cmmnt: string;
}

export interface ProposedDisbursementRepaymentRowDto {
  clntSeq: number;
  crdtPrpsdRpymntDsbmntSeq: number;
  crdtPrpslSeq: number;
  crtdBy: string | null;
  crtdDt: string | null;
  delFlg: number;
  lastUpdBy: string | null;
  lastUpdDt: string | null;
  maxExpsr: number;
  prpsdDsbmnt: number;
  prpsdRpymnt: number;
  schDsbmnt: number;
  schMth: string;
  schRpymnt: number;
  /** Repayment term type: ref code group 13 `refCd` (17 Conventional, 18 Islamic, ...). */
  rpymntTerm?: number | null;
}

export interface ProposedRepaymentResponseDto {
  clntSeq: number;
  crdtPrpslSeq: number;
  crtdBy: string | null;
  crtdDt: string | null;
  delFlg: number;
  lastUpdBy: string | null;
  lastUpdDt: string | null;
  prpsdDsbmntSeq: number;
  prpsdRpymntAmnt: number;
  prpsdRpymntSeq: number;
  schRpymntMth: string;
}

export interface ExistingAgreementScheduleDto {
  month: string;
  scheduledDisbursement: number;
  scheduledRepayment: number;
}
export interface ProposedDisbursementPayload {
  prpsdDsbmntSeq: number | null;
  schMth: string;
  prpsdDsbmntAmt: number;
  delFlg: number;
}

export interface ProposedDisbursementTermPayload {
  disbursements: ProposedDisbursementPayload[];

  /** Repayment term type (PKL_GRP.REPATMENTS_TERMS) this group of disbursements belongs to. */
  rpymntTerm: number;
  delflg: number;
}

export interface SaveProposedDisbursementPayload {
  crdtPrpslSeq: number;
  terms: ProposedDisbursementTermPayload[];
}

export interface RepaymentDisbursementSummaryRowPayload {
  rpymntPlnSeq: number | null;
  schMth: string;
  prpsdDsbmtAmt: number;
  prpsdRpmtAmt: number;
  schRpmtAmt: number;
  schDsbmtAmt: number;
  maxExpsrAmt: number;
  prpsdDsbmntSeq: number | null;
  prpsdRpymntSeq: number | null;
  stsKey: number;
}

export interface SaveRepaymentDisbursementSummaryPayload {
  crdtPrpslSeq: number;
  rows: RepaymentDisbursementSummaryRowPayload[];
  totalDisbursement: number;
  totalRepayment: number;
}

export interface CalculateDisbursementRepaymentPlanItemPayload {
  schMth: string;
  prpsdDsbmntAmt: number;
  prpsdDsbmntSeq: number | null;
}

export interface CalculateDisbursementRepaymentPlanTermPayload {
  /** Repayment term type (PKL_GRP.REPATMENTS_TERMS) this group of disbursements belongs to. */
  rpymntTerm: number;
  disbursements: CalculateDisbursementRepaymentPlanItemPayload[];
}

export interface CalculateDisbursementRepaymentPlanPayload {
  crdtPrpslSeq: number;
  gracePeriod: number;
  numberOfInstallments: number;
  arrear: number;
  frequency: number;
  prpsdMaxExpsr: number;
  totalAmount: number;
  prevMaxExposure: number;
  terms: CalculateDisbursementRepaymentPlanTermPayload[];
}

/** Payload for POST /clclt-dsbrsmnt-repayment-plan (flat disbursements, no terms grouping). */
export interface SaveDisbursementRepaymentPlanPayload {
  crdtPrpslSeq: number;
  gracePeriod: number;
  numberOfInstallments: number;
  arrear: number;
  frequency: number;
  prpsdMaxExpsr: number;
  totalAmount: number;
  prevMaxExposure: number;
  disbursements: CalculateDisbursementRepaymentPlanItemPayload[];
}
export interface CreditFinancialDataItemDto {
  crdtFnclDtaSeq: number | null;
  valSeq: number;
  val: number;
  delFlg: number;

  /**
   * 0 = Audited
   * 1 = Unaudited
   */
  fnclType: number;

  fncYr: number;

  /**
   * Financial master type sequence.
   * Example: section.fncTypSeq
   */
  typ: 1;
}

export interface SaveCreditFinancialDataPayload {
  crdtPrpslSeq: number;
  rprtngCrncy: number;
  fnclYrEnd: string;
  cnsldtdFncl: number;
  instNm: string;
  crdtFnclDtaLst: CreditFinancialDataItemDto[];
}

export interface CreditTermParameterGuarantorDto {
  crdtGrntrSeq: number | null;
  grntrKey: number;
  share: number;
  delFlg: number;
}

export interface CreditTermParameterDto {
  crdtTrmParamsSeq: number | null;
  typ: number;
  dsbmntAmount: number;
  rnwAblAmnt: number;
  rnwAblFlg: number;
  prmAmnt: number;
  prmFlg: number;
  vkbrFlg: number;
  vKbrStrt: number;
  kbrRstPrd: number;
  sprdRt: number;
  grntrFlg: number;
  crdtTrmParamsGrntrList: CreditTermParameterGuarantorDto[];
  delFlg: number;
  dsbStrtDt: string | null;
  dsbEndDt: string | null;
}
export interface FinancialAnalysisRow {
  institution: string;
  sortOrder: number;
  fncTypSeq: number;
  fncValSeq: number;
  head: string;
  dscr: string;
  periodValues: Record<string, number | null>;
}

export interface SelectableFinancialAnalysisRow extends FinancialAnalysisRow {
  selected: boolean;
}

export interface SaveCreditTermParametersPayload {
  crdtPrpslSeq: number;
  rpStrtDt: string | null;
  rpEndDt: string | null;
  rpymntFrqncy: number;
  sprdRt: number;
  arrearFlg: number;
  upfrntFlg: number;
  upFrntFee: number;
  crdtEnsrFee: number;
  grcPrd: number;
  noOfInstlmnt: number;
  prpsdExpsr: number;
  crdtTrmParamsLst: CreditTermParameterDto[];
}
export interface FinancialAnalysisRow {
  institution: string;
  sortOrder: number;
  fncTypSeq: number;
  fncValSeq: number;
  head: string;
  dscr: string;
  periodValues: Record<string, number | null>;
}

export interface SelectableFinancialAnalysisRow extends FinancialAnalysisRow {
  selected: boolean;
}

export interface FinancialAnalysisSectionJson {
  type: 'financial-analysis-details';
  institutionName: string;
  periodColumns: string[];
  rows: FinancialAnalysisRow[];
}

/**
 * `refGrp` is optional only for backward compatibility with existing callers
 * (e.g. paragraph/risk/summary-terms sections) that still send it; the
 * `/crdt-prpsl/save-sctions` Swagger contract itself does not require it.
 * `txtData` allows null because bullet-list-section already sends null.
 */
export interface SaveCreditProposalBlock {
  crdtPrpslBlkSeq: number | null;
  refGrp?: number;
  refValSeq: number;
  blkTyp: number;
  delFlg: number;
  sortOrdr: number;
  txtData: string | null;
  jsonData: unknown[];
}

export interface SaveCreditProposalSectionPayload {
  crprtOnbrdngSeq: number;
  crdtPrpslSeq: number | null;
  blockList: SaveCreditProposalBlock[];
}

export interface CovenantQuestionItemPayload {
  crdtCnvntQstnSeq: number | null;
  qstnSeq: number;
  delFlg: number;
}

export interface SaveCovenantQuestionsPayload {
  crdtPrpslSeq: number;
  questions: CovenantQuestionItemPayload[];
}

export interface CovenantQuestionResponseDto {
  crdtCnvntQstnSeq: number;
  crdtPrpslSeq: number;
  qstnSeq: number;
  delFlg: number;
  crtdBy: string | null;
  crtdDt: string | null;
  lastUpdBy: string | null;
  lastUpdDt: string | null;
}

export type SectionSaveStatus =
  | 'idle'
  | 'dirty'
  | 'saving'
  | 'saved'
  | 'validation-error'
  | 'api-error';

export interface SectionSaveResult {
  sectionId: string;
  status: SectionSaveStatus;
  message?: string;
}

@Injectable({
  providedIn: 'root',
})
export class CraditService {
  constructor(private api: ApiService) { }

  saveSectionTrigger$ = new Subject<string>();

  /** Fired when the Credit Proposal's uploaded documents (re)load or change. */
  readonly documentsChanged$ = new Subject<void>();

  /** Active guarantors from setup, used by the repayment term Guarantor list. */
  getActiveGuarantorList(): Observable<any> {
    return this.api.get(API_HOST.setup, ENDPOINTS.SETUP.grntr_ACT_LIST);
  }

  /**
   * True only while the document builder's "Save and Continue" save is
   * running, so a section can apply checks that "Save Draft" skips.
   */
  isSaveAndContinue = false;

  // ✅ Child sections report the outcome of a triggered save back here
  private readonly sectionSaveResultSubject = new Subject<SectionSaveResult>();
  readonly sectionSaveResult$ = this.sectionSaveResultSubject.asObservable();

  reportSectionSaveResult(result: SectionSaveResult): void {
    this.sectionSaveResultSubject.next(result);
  }

  // ✅ Shared crdtPrpslSeq across all sections
  private crdtPrpslSeqSubject = new BehaviorSubject<number | null>(null);
  crdtPrpslSeq$ = this.crdtPrpslSeqSubject.asObservable();

  setCrdtPrpslSeq(seq: number | null | undefined): void {
    if (seq != null && seq !== this.crdtPrpslSeqSubject.value) {
      this.crdtPrpslSeqSubject.next(seq);
    }
  }

  getCrdtPrpslSeq(): number {
    return this.crdtPrpslSeqSubject.value ?? 0;
  }

  resetCrdtPrpslSeq(): void {
    this.crdtPrpslSeqSubject.next(null);
  }

  // ✅ Whether the tentative repayment/disbursement plan has already been
  // saved for a given proposal — scoped per crdtPrpslSeq (the backend only
  // allows this save once per proposal). Persisted per crdtPrpslSeq in
  // localStorage so a refresh/reopen of the same proposal keeps
  // "Save and Review Report" disabled and "Submit Credit Proposal" visible;
  // a different (or new) proposal never inherits another proposal's state
  // because every read/write is keyed by its own crdtPrpslSeq.
  private readonly repaymentPlanCalculatedKeyPrefix = 'repaymentPlanCalculated:';

  private readonly repaymentPlanCalculatedSubject = new Subject<{
    crdtPrpslSeq: number;
    calculated: boolean;
  }>();

  readonly repaymentPlanCalculated$ = this.repaymentPlanCalculatedSubject.asObservable();

  setRepaymentPlanCalculated(crdtPrpslSeq: number, value: boolean): void {
    if (!crdtPrpslSeq) {
      return;
    }

    if (value) {
      try {
        localStorage.setItem(`${this.repaymentPlanCalculatedKeyPrefix}${crdtPrpslSeq}`, 'true');
      } catch {
        // Ignore storage failures (e.g. private browsing) — the in-memory
        // emission below still updates the UI for the rest of this session.
      }
    }

    this.repaymentPlanCalculatedSubject.next({ crdtPrpslSeq, calculated: value });
  }

  isRepaymentPlanCalculated(crdtPrpslSeq: number): boolean {
    if (!crdtPrpslSeq) {
      return false;
    }

    try {
      return (
        localStorage.getItem(`${this.repaymentPlanCalculatedKeyPrefix}${crdtPrpslSeq}`) === 'true'
      );
    } catch {
      return false;
    }
  }

  triggerSave(sectionId: string): void {
    this.saveSectionTrigger$.next(sectionId);
  }

  // ✅ SAVE
  saveCreditProposal(payload: any): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_CRDT_PRPSL_LIST, payload);
  }

  /** POST /crdt-prpsl/save-crdt-prpsl — used to soft-delete a credit proposal (delFlg: 1). */
  saveCrdtPrpsl(payload: {
    crdtPrpslSeq: number;
    crptOnbrdngSeq: number;
    stsKey: number;
    delFlg: number;
  }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_CRDT_PRPSL, payload);
  }

  getSections(onboardingSeq: number, refValSeq: number) {
    return this.api.get(API_HOST.cradit, `/get-sctions/${onboardingSeq}?refValSeq=${refValSeq}`);
  }
  getProposedRepayments(
    crdtPrpslSeq: number,
  ): Observable<ProposedRepaymentResponseDto[]> {
    return this.api.get<ProposedRepaymentResponseDto[]>(
      API_HOST.repayment,
      `${ENDPOINTS.REPAYMENT.GET_PRPSD_RPYMNT}${crdtPrpslSeq}`,
    );
  }

  getCreditProposalList(crptOnbrdngSeq: number, refGrp: number): Observable<any> {
    const url = `${ENDPOINTS.CRADIT.GET_CRDT_PRPSL_LIST}/${crptOnbrdngSeq}/${refGrp}`;
    return this.api.get(API_HOST.cradit, url);
  }

  getAllSection(crdtPrpsl: number): Observable<any> {
    return this.api.get(API_HOST.cradit, `${ENDPOINTS.CRADIT.GET_ALL_SECTION}/${crdtPrpsl}`);
  }
  getCrdtBlck(crdtPrpslSeq: number, refValSeq: number[]): Observable<any> {
    const params = refValSeq.map((v) => `refValSeq=${v}`).join('&');

    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_CRDT_BLCK}/${crdtPrpslSeq}?${params}`,
    );
  }

  saveCreditProposalRisk(payload: any): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_CRDT_PRPSL_RISK, payload);
  }

  getCreditProposalRiskList(crptOnbrdngSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_CRDT_PRPSL_RISK_LIST}/${crptOnbrdngSeq}`,
    );
  }

  saveCreditProposalTable(payload: any) {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_CRDT_PRPSL_TBL, payload);
  }

  saveSection(payload: SaveCreditProposalSectionPayload): Observable<unknown> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_SECTIONS_CRDIT_PERPOSAL, payload);
  }
  getFinancialAnalysis(crdtPrpslSeq: number): Observable<FinancialAnalysisRow[]> {
    return this.api.get<FinancialAnalysisRow[]>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FINANCIAL_ANALYSIS}?crdtPrpslSeq=${crdtPrpslSeq}`,
    );
  }
  /** GET /crdt-prpsl/get-financial-highlights/{crdtPrpslSeq} — the proposal's Latest Financials */
  getFinancialHighlights(crdtPrpslSeq: number): Observable<unknown> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FINANCIAL_HIGHLIGHTS}${crdtPrpslSeq}`,
    );
  }
  getCreditProposalTable(onboardingSeq: number, refVal: number) {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_CRDT_PRPSL_TBL}/${onboardingSeq}/${refVal}`,
    );
  }

  saveFinancialData(payload: SaveCreditFinancialDataPayload): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_FINACL_DATA, payload);
  }
  getAllCreditProposalList(): Observable<any[]> {
    return this.api.get<any[]>(API_HOST.cradit, ENDPOINTS.CRADIT.GET_CRDT_PRPSL_LIST);
  }

  getFinancialData(crdtPrpslSeq: number, typ: number): Observable<any> {
    return this.api.get(API_HOST.cradit, `${ENDPOINTS.CRADIT.GET_FINACL_DATA}/${crdtPrpslSeq}/${typ}`);
  }
  saveTermParameters(payload: SaveCreditTermParametersPayload): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_TERM_PARAMETERS, payload);
  }

  getCreditTermParameters(crdtPrpslSeq: number): Observable<CreditTermParamsDto> {
    return this.api.get<CreditTermParamsDto>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_CRDT_TRMS_PRMS}${crdtPrpslSeq}`,
    );
  }

  /** GET /crdt-prpsl/get-fo-ltr-params/{crdtPrpslSeq} — offer letter header/body values. */
  getFacilityOfferLetterParams(crdtPrpslSeq: number): Observable<FacilityOfferLetterParamsDto> {
    return this.api.get<FacilityOfferLetterParamsDto>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FO_LTR_PARAMS}${crdtPrpslSeq}`,
    );
  }

  getPreviousMaxExposure(crdtPrpslSeq: number): Observable<any> {
    return this.api.get(API_HOST.cradit, `${ENDPOINTS.CRADIT.MAX_PRVS_EXPOR}${crdtPrpslSeq}`);
  }

  getExistingRepayment(crdtPrpslSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_EXISTING_REPYMNT}${crdtPrpslSeq}`,
    );
  }

  /** GET /crdt-prpsl/get-crdt-fo-ltr — proposals available for a facility offer letter */
  getCreditProposalsForLetter(): Observable<CreditProposalForLetterDto[]> {
    return this.api.get<CreditProposalForLetterDto[]>(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.GET_CRDT_FO_LTR,
    );
  }

  /** GET /crdt-prpsl/fo-ltr/aprvd-lst — approved facility offer letters */
  getApprovedCreditProposalsForLetter(): Observable<CreditProposalForLetterDto[]> {
    return this.api.get<CreditProposalForLetterDto[]>(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.GET_APRVD_FO_LTR,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/save-doc — uploads/saves a facility offer letter document (multipart) */
  saveFoLtrDoc(payload: FormData): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAVE_FO_LTR_DOC, payload);
  }

  /** GET /crdt-prpsl/fo-ltr/get-fo-ltr-doc — saved documents for a facility offer letter */
  getFoLtrDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FO_LTR_DOC}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** GET /crdt-prpsl/fo-ltr/get-fo-ltr-doc-hstry/{crdtFoLtrSeq}/{docSeq} — upload history of one offer letter document type */
  getFoLtrDocHistory(crdtFoLtrSeq: number, docSeq: number): Observable<any> {
    const base = ENDPOINTS.CRADIT.GET_FO_LTR_DOC_HISTORY.replace(/\/$/, '');

    return this.api.get(API_HOST.cradit, `${base}/${crdtFoLtrSeq}/${docSeq}`);
  }

  /** GET /crdt-prpsl/fo-ltr/get-fo-ltr-issu-doc — Required Documents checklist */
  getFoLtrIssuanceDocs(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FO_LTR_ISSU_DOC}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** GET /crdt-prpsl/fo-ltr/pending-draft-docs?crdtFoLtrSeq= — one offer letter's draft documents pending the Relationship Officer (LNLY_RO) */
  getPendingDraftFoLtrDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.PENDING_DRAFT_FO_LTR_DOCS}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** GET /crdt-prpsl/fo-ltr/pending-draft-clnt-lst — clients with draft offer letter documents pending the Relationship Officer (LNLY_RO) */
  getPendingDraftFoLtrClientList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.PENDING_DRAFT_FO_LTR_CLNT_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/ro-acptnc-pndng-clnt-lst — clients whose offer letter acceptance is pending the Relationship Officer (LNLY_RO) */
  getRoAcceptancePendingClientList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.RO_ACCEPTANCE_PENDING_CLNT_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/ro-acptnc-pndng-docs?crdtFoLtrSeq= — one offer letter's documents pending the Relationship Officer's acceptance */
  getRoAcceptancePendingDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.RO_ACCEPTANCE_PENDING_DOCS}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/ro-approve-draft-docs?crdtFoLtrDocSeq= — Relationship Officer approves a draft document */
  approveDraftFoLtrDocument(crdtFoLtrDocSeq: number): Observable<any> {
    return this.api.post(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.RO_APPROVE_DRAFT_FO_LTR_DOCS}?crdtFoLtrDocSeq=${crdtFoLtrDocSeq}`,
      {},
    );
  }

  /** POST /crdt-prpsl/fo-ltr/add-recommendation — records the changes a draft document needs */
  addFoLtrRecommendation(payload: { crdtFoLtrDocSeq: number; cmmnt: string }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.ADD_FO_LTR_RECOMMENDATION, payload);
  }

  /** GET /crdt-prpsl/fo-ltr/rcmnd-required-lst — documents whose recommendations the VCM (LNLY_VCM) must act on */
  getClientRecommendationList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.FO_LTR_RCMND_REQUIRED_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/mrc-brc-required-clnt-lst — clients listed on the Extention List tab */
  getFoLtrExtensionList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.FO_LTR_MRC_BRC_REQUIRED_CLNT_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/mrc-brc-required-docs?crdtFoLtrSeq= — the open Extention List client's documents */
  getFoLtrExtensionDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.FO_LTR_MRC_BRC_REQUIRED_DOCS}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/xtnsn-request — extension request for an Extention List document */
  requestFoLtrExtension(payload: {
    crdtFoLtrSeq: number;
    docSeq: number;
    cmmnt: string;
  }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.FO_LTR_XTNSN_REQUEST, payload);
  }

  /** GET /crdt-prpsl/fo-ltr/xtnsn-pending-clnt-lst — clients listed on the Pending Extentions tab */
  getFoLtrPendingExtensionList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.FO_LTR_XTNSN_PENDING_CLNT_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/xtnsn-pending-lst?crdtFoLtrSeq= — the open Pending Extentions client's documents */
  getFoLtrPendingExtensionDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.FO_LTR_XTNSN_PENDING_LST}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/xtnsn-grant — approve a pending extension request */
  grantFoLtrExtension(payload: {
    crdtFoLtrDocXtnsnSeq: number;
    tillDt: string;
    cmmnt: string;
  }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.FO_LTR_XTNSN_GRANT, payload);
  }

  /** POST /crdt-prpsl/fo-ltr/xtnsn-reject — reject a pending extension request */
  rejectFoLtrExtension(payload: {
    crdtFoLtrDocXtnsnSeq: number;
    tillDt: string;
    cmmnt: string;
  }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.FO_LTR_XTNSN_REJECT, payload);
  }

  /** GET /crdt-prpsl/fo-ltr/fo-ltr-rvw/{entySeq}/{entyTyp} — review comments on a facility offer letter document */
  getFoLtrReview(entySeq: number, entyTyp: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FO_LTR_REVIEW}${entySeq}/${entyTyp}`,
    );
  }

  /** GET /crdt-prpsl/fo-ltr/trm-params/{crdtFoLtrSeq} — term parameters of a facility offer letter */
  getFoLtrTermParams(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_FO_LTR_TRM_PARAMS}${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/print-fo-ltr/{crdtFoLtrSeq} */
  printFoLtr(crdtFoLtrSeq: number): Observable<any> {
    return this.api.post(API_HOST.cradit, `${ENDPOINTS.CRADIT.PRINT_FO_LTR}${crdtFoLtrSeq}`, {});
  }

  /** GET /crdt-prpsl/fo-ltr/trm-params-fol/{crdtFoLtrSeq} — the offer letter's own term parameters (null when none saved) */
  getTrmParamsFol(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(API_HOST.cradit, `${ENDPOINTS.CRADIT.GET_TRM_PARAMS_FOL}${crdtFoLtrSeq}`);
  }

  // ================= FOL NEW FLOW — STAGE 2 (issue-flag documents) =================
  /** GET /crdt-prpsl/fo-ltr/sac-pndng-clnt-lst — clients with issue documents pending SAC review */
  getSacPendingFoLtrClientList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.SAC_PENDING_FO_LTR_CLNT_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/pndng-lgl-opnn-lst — offer letters whose legal opinion the AM must upload */
  getPendingLegalOpinionList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.PENDING_LEGAL_OPINION_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/sac-pndng-lst?crdtFoLtrSeq= — one offer letter's documents at 4812 */
  getSacPendingFoLtrDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.SAC_PENDING_FO_LTR_DOCS}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/sac-aprv — SAC approves an issue document (4812 → 2658) */
  sacApproveFoLtrDocument(payload: { crdtFoLtrDocSeq: number; cmmnt: string }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAC_APPROVE_FO_LTR_DOC, payload);
  }

  /** POST /crdt-prpsl/fo-ltr/sac-rjct — SAC rejects an issue document (→ 765, comment required) */
  sacRejectFoLtrDocument(payload: { crdtFoLtrDocSeq: number; cmmnt: string }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SAC_REJECT_FO_LTR_DOC, payload);
  }

  /** GET /crdt-prpsl/fo-ltr/ro-pndng-clnt-lst — clients with issue documents / signed copies pending the RO */
  getRoPendingFoLtrClientList(): Observable<any> {
    return this.api.get(API_HOST.cradit, ENDPOINTS.CRADIT.RO_PENDING_FO_LTR_CLNT_LST);
  }

  /** GET /crdt-prpsl/fo-ltr/ro-pndng-lst?crdtFoLtrSeq= — one offer letter's documents at 2658 / 4815 */
  getRoPendingFoLtrDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.RO_PENDING_FO_LTR_DOCS}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/ro-doc-aprv — RO approves an issue document (2658 → 4813) or its signed copy (4815 → 902) */
  roApproveFoLtrDocument(payload: { crdtFoLtrDocSeq: number; cmmnt: string }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.RO_APPROVE_FO_LTR_DOC, payload);
  }

  /** POST /crdt-prpsl/fo-ltr/ro-doc-rcmnd — RO sends a document back to the AM with a recommendation (→ 4814) */
  roRecommendFoLtrDocument(payload: { crdtFoLtrDocSeq: number; cmmnt: string }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.RO_RECOMMEND_FO_LTR_DOC, payload);
  }

  /** GET /crdt-prpsl/fo-ltr/am-doc-pndng-lst?crdtFoLtrSeq= — documents waiting on the AM (4813 signed copy due, 4814 recommendation) */
  getAmDocPendingFoLtrDocuments(crdtFoLtrSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.AM_DOC_PENDING_FO_LTR_DOCS}?crdtFoLtrSeq=${crdtFoLtrSeq}`,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/update-hrd-cpy-dt — AM records a document's hard copy date */
  updateFoLtrHardCopyDate(payload: { crdtFoLtrDocSeq: number; hrdCpyDt: string }): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.UPDATE_FO_LTR_HRD_CPY_DT, payload);
  }

  /** POST /crdt-prpsl/fo-ltr/vcm-approve-recommendation — VCM saves the reviewed term parameters */
  vcmApproveFoLtrRecommendation(payload: any): Observable<any> {
    return this.api.post(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.VCM_APPROVE_FO_LTR_RECOMMENDATION,
      payload,
    );
  }

  /** POST /crdt-prpsl/fo-ltr/vcm-reject-recommendation — VCM rejects the recommendation with a comment */
  vcmRejectFoLtrRecommendation(payload: { crdtFoLtrDocSeq: number; cmmnt: string }): Observable<any> {
    return this.api.post(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.VCM_REJECT_FO_LTR_RECOMMENDATION,
      payload,
    );
  }

  /** GET /crdt-prpsl/get-prpsd-dsbmnt-list — every proposed disbursement instalment */
  getProposedDisbursementList(): Observable<ProposedDisbursementListRowDto[]> {
    return this.api.get<ProposedDisbursementListRowDto[]>(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.GET_PRPSD_DSBMNT_LIST,
    );
  }

  /** GET /crdt-prpsl/get-pending-prpsd-dsbmnt-list — pending proposed disbursements (shown first in the list) */
  getPendingProposedDisbursementList(): Observable<ProposedDisbursementListRowDto[]> {
    return this.api.get<ProposedDisbursementListRowDto[]>(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.GET_PENDING_PRPSD_DSBMNT_LIST,
    );
  }

  /** GET /dsbmnt-note/get-prpsd-dsbmnt/{prpsdDsbmntSeq} — disbursement note detail */
  getProposedDisbursementNote(prpsdDsbmntSeq: number): Observable<ProposedDisbursementNoteDto> {
    return this.api.get<ProposedDisbursementNoteDto>(
      API_HOST.disbursement_note,
      `${ENDPOINTS.DISBURSEMENT_NOTE.GET_PRPSD_DSBMNT}${prpsdDsbmntSeq}`,
    );
  }

  /** GET /crdt-prpsl/get-crdt-grntrs/{crdtPrpslSeq} — guarantee calculation figures */
  /**
   * GET /payment/recovery/get-clnt-otstndng/{clntSeq}/{fcltyTypKey} — outstanding
   * of each of the client's facilities of one facility type (17 Conventional,
   * 18 Islamic, ...) plus their total.
   */
  getClientOutstanding(clntSeq: number, fcltyTypKey: number): Observable<unknown> {
    return this.api.get(
      API_HOST.payment_recovery,
      `${ENDPOINTS.RECOVERY.GET_CLNT_OTSTNDNG}${clntSeq}/${fcltyTypKey}`,
    );
  }

  getCreditGuarantors(crdtPrpslSeq: number): Observable<CreditGuarantorsResponseDto> {
    return this.api.get<CreditGuarantorsResponseDto>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_CRDT_GRNTRS}${crdtPrpslSeq}`,
    );
  }

  /** GET /crdt-prpsl/get-covenant-responses/{crdtPrpslSeq} — covenant compliance rows */
  getCovenantResponses(crdtPrpslSeq: number): Observable<CovenantResponseDto[]> {
    return this.api.get<CovenantResponseDto[]>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_COVENANT_RESPONSES}${crdtPrpslSeq}`,
    );
  }

  /**
   * GET /crdt-prpsl/get-prev-cnvnt-rsp/{crdtPrpslSeq} — covenant responses
   * saved earlier for this client (its previous credit proposal), used to
   * pre-fill Summary Terms' Covenants when this proposal has none saved yet.
   */
  getPreviousCovenantResponses(
    crdtPrpslSeq: number,
  ): Observable<CovenantResponseDto[] | null> {
    return this.api.get<CovenantResponseDto[] | null>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_PREVIOUS_COVENANT_RESPONSES}${crdtPrpslSeq}`,
    );
  }

  /**
   * GET /crdt-prpsl/get-cnvnt-rsp-by-clnt/{clntSeq} — every covenant response
   * recorded for a client, across its credit proposals. Each row carries its
   * own `crdtPrpslSeq`, which is what a save has to be grouped by.
   */
  getCovenantResponsesByClient(clntSeq: number): Observable<CovenantResponseDto[]> {
    return this.api.get<CovenantResponseDto[]>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_COVENANT_RESPONSES_BY_CLIENT}${clntSeq}`,
    );
  }

  /** POST /dsbmnt-note/submit-dsmbmnt-note/{prpsdDsbmntSeq} — final submit of the note */
  /** POST /onboarding/save-rvw — saves a review comment against any entity. */
  saveEntityReview(payload: { entySeq: number; entyType: number; cmmnt: string }): Observable<any> {
    return this.api.post(API_HOST.onboarding, ENDPOINTS.ONBOARDING.SAVE_RVW, payload);
  }

  /** GET /onboarding/ro-rvw/{entySeq}/{entyTyp} — the entity's saved RO review comments. */
  getRoReviews(entySeq: number, entyTyp: number): Observable<any> {
    return this.api.get(
      API_HOST.onboarding,
      `${ENDPOINTS.ONBOARDING.GET_RO_RVW}/${entySeq}/${entyTyp}`,
    );
  }

  submitDisbursementNote(
    prpsdDsbmntSeq: number,
    payload: SubmitDisbursementNotePayload,
  ): Observable<any> {
    return this.api.post(
      API_HOST.disbursement_note,
      `${ENDPOINTS.DISBURSEMENT_NOTE.SUBMIT_DSBMNT_NOTE}${prpsdDsbmntSeq}`,
      payload,
    );
  }

  /** POST /crdt-prpsl/save-covenant-responses */
  saveCovenantResponses(payload: SaveCovenantResponsesPayload): Observable<any> {
    return this.api.post(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.SAVE_COVENANT_RESPONSES,
      payload,
    );
  }

  /** GET /dsbmnt-note/get-cad-rsp/{prpsdDsbmntSeq} — saved CAD checklist answers */
  getCadResponses(prpsdDsbmntSeq: number): Observable<CadResponseDto[]> {
    return this.api.get<CadResponseDto[]>(
      API_HOST.disbursement_note,
      `${ENDPOINTS.DISBURSEMENT_NOTE.GET_CAD_RSP}${prpsdDsbmntSeq}`,
    );
  }

  /** GET /dsbmnt-note/get-grntr-lmt-utlzd/{prpsdDsbmntSeq} — guarantor limits utilized / remaining */
  getGuarantorLimitUtilized(prpsdDsbmntSeq: number): Observable<GuarantorLimitUtilizedDto[]> {
    return this.api.get<GuarantorLimitUtilizedDto[]>(
      API_HOST.disbursement_note,
      `${ENDPOINTS.DISBURSEMENT_NOTE.GET_GRNTR_LMT_UTLZD}${prpsdDsbmntSeq}`,
    );
  }

  /** POST /dsbmt/sv-dsbmnt-adj — saves the recommended amount of a proposed disbursement */
  saveRecommendedAmount(payload: { prpsdDsbmntSeq: number; rcmndedAmnt: number }): Observable<any> {
    return this.api.post(API_HOST.disbursement, ENDPOINTS.DISBURSEMENT.SAVE_DSBMNT_ADJ, payload);
  }

  /** POST /dsbmnt-note/save-prpsd-dsbmnt-grntr — create / update / delete a guarantor share */
  saveDisbursementGuarantor(payload: SaveDisbursementGuarantorPayload): Observable<any> {
    return this.api.post(
      API_HOST.disbursement_note,
      ENDPOINTS.DISBURSEMENT_NOTE.SAVE_PRPSD_DSBMNT_GRNTR,
      payload,
    );
  }

  /** GET /dsbmnt-note/get-prpsd-dsbmnt-grntr/{prpsdDsbmntSeq} — saved guarantor shares */
  getDisbursementGuarantors(prpsdDsbmntSeq: number): Observable<DisbursementGuarantorDto[]> {
    return this.api.get<DisbursementGuarantorDto[]>(
      API_HOST.disbursement_note,
      `${ENDPOINTS.DISBURSEMENT_NOTE.GET_PRPSD_DSBMNT_GRNTR}${prpsdDsbmntSeq}`,
    );
  }

  /** GET /dsbmnt-note/get-clnt-grntrs/{prpsdDsbmntSeq} — the client's guarantors for a disbursement */
  getClientGuarantors(prpsdDsbmntSeq: number): Observable<unknown> {
    return this.api.get(
      API_HOST.disbursement_note,
      `${ENDPOINTS.DISBURSEMENT_NOTE.GET_CLNT_GRNTRS}${prpsdDsbmntSeq}`,
    );
  }

  /** POST /dsbmnt-note/save-cad-rsp — CAD checklist answers of a disbursement */
  saveCadResponses(payload: SaveCadResponseItem[]): Observable<any> {
    return this.api.post(
      API_HOST.disbursement_note,
      ENDPOINTS.DISBURSEMENT_NOTE.SAVE_CAD_RSP,
      payload,
    );
  }

  /** POST /crdt-prpsl/save-covenant-questions */
  saveCovenantQuestions(payload: SaveCovenantQuestionsPayload): Observable<any> {
    return this.api.post(
      API_HOST.cradit,
      ENDPOINTS.CRADIT.SAVE_COVENANT_QUESTIONS,
      payload,
    );
  }

  /** GET /crdt-prpsl/get-covenant-questions/{crdtPrpslSeq} — previously saved covenant question selections */
  getCovenantQuestions(crdtPrpslSeq: number): Observable<CovenantQuestionResponseDto[]> {
    return this.api.get<CovenantQuestionResponseDto[]>(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_COVENANT_QUESTIONS}${crdtPrpslSeq}`,
    );
  }

  getExistingDisbursement(crdtPrpslSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.cradit,
      `${ENDPOINTS.CRADIT.GET_EXISTING_DSBMNT}${crdtPrpslSeq}`,
    );
  }

  submitCreditProposal(payload: SubmitCreditProposalPayload): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.SUBMT_CRDT_PRPOSL, payload);
  }

  saveProposedDisbursements(payload: SaveProposedDisbursementPayload): Observable<any> {
    return this.api.post(API_HOST.repayment, ENDPOINTS.REPAYMENT.SAVE_PRPSD_DSBMNT, payload);
  }

  getProposedDisbursements(crdtPrpslSeq: number): Observable<any> {
    return this.api.get(
      API_HOST.repayment,
      `${ENDPOINTS.REPAYMENT.GET_PRPSD_DSBMNT}${crdtPrpslSeq}`,
    );
  }

  /** GET /rpymnt-plan/get-prpsd-dsbmnt-rpymnt/{crdtPrpslSeq} — consolidated monthly disbursement/repayment/exposure rows. */
  getProposedDisbursementRepayment(
    crdtPrpslSeq: number,
  ): Observable<ProposedDisbursementRepaymentRowDto[]> {
    return this.api.get<ProposedDisbursementRepaymentRowDto[]>(
      API_HOST.repayment,
      `${ENDPOINTS.REPAYMENT.GET_PRPSD_DSBMNT_RPYMNT}${crdtPrpslSeq}`,
    );
  }

  getMaxExposure(crdtPrpslSeq: number): Observable<any> {
    return this.api.get(API_HOST.cradit, `${ENDPOINTS.CRADIT.MAX_EXPOURE}${crdtPrpslSeq}`);
  }

  calculateDisbursementRepaymentPlan(
    payload: CalculateDisbursementRepaymentPlanPayload,
  ): Observable<any> {
    return this.api.post(API_HOST.cradit, ENDPOINTS.CRADIT.CALCULATE_REPAYMENT_PLAN, payload);
  }
}
